import { cn } from "@/lib/utils";
import PhoneFrame from "./PhoneFrame";
import { ArrowLeft, Brain, CircleCheck, ChevronRight, Zap, Brain as BrainIcon, Wind, Compass, Mic, Heart } from "lucide-react";


// ── Browser chrome (for dashboard) ───────────────────────────────────────────
const BrowserFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-3xl border border-border/60 bg-card shadow-[var(--shadow-elevated)] overflow-hidden">
    <div className="flex items-center gap-1.5 px-4 py-3 bg-muted/40 border-b border-border/40">
      <span className="w-3 h-3 rounded-full bg-rose-300" />
      <span className="w-3 h-3 rounded-full bg-amber-300" />
      <span className="w-3 h-3 rounded-full bg-emerald-300" />
      <span className="ml-3 flex-1 text-center text-xs text-muted-foreground font-medium truncate">
        app.hummany.com / dashboard
      </span>
    </div>
    <div className="bg-[var(--color-surface,theme(colors.card))]">
      {children}
    </div>
  </div>
);

// ── Emotion colors ────────────────────────────────────────────────────────────
const emotionBg: Record<string, string> = {
  happy:      "bg-emotion-happy",
  calm:       "bg-emotion-calm",
  neutral:    "bg-emotion-neutral",
  tired:      "bg-emotion-tired",
  stressed:   "bg-emotion-stressed",
  anxiety:    "bg-emotion-anxiety",
  frustrated: "bg-emotion-frustrated",
  sad:        "bg-emotion-sad",
};

// ── Check-in mockup ───────────────────────────────────────────────────────────
export const MockupCheckin = () => {
  const emotions = [
    { id: "happy",      label: "Feliz"       },
    { id: "calm",       label: "Calmado"     },
    { id: "neutral",    label: "Neutro"      },
    { id: "tired",      label: "Cansado"     },
    { id: "stressed",   label: "Estresado"   },
    { id: "anxiety",    label: "Ansioso"     },
    { id: "frustrated", label: "Frustrado"   },
    { id: "sad",        label: "Desmotivado" },
  ];

  return (
    <PhoneFrame>
      <div className="flex flex-col gap-4 px-5 pb-6 bg-background" style={{ height: 695, paddingTop: 56 }}>
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Hola, Lucía</p>
          <h3 className="text-xl font-semibold text-foreground mt-1">¿Cómo te sientes ahora?</h3>
          <p className="text-xs text-muted-foreground mt-1">Sin juicios, solo para acompañarte</p>
        </div>

        {/* Emotion grid — smaller circles */}
        <div className="grid grid-cols-4 gap-2">
          {emotions.map((e, i) => (
            <div
              key={e.id}
              className={cn(
                "flex flex-col items-center gap-1.5 p-2 rounded-2xl",
                i === 0 ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""
              )}
            >
              <div className={cn("w-10 h-10 rounded-full", emotionBg[e.id])} />
              <span className="text-[10px] font-medium text-foreground text-center leading-tight">{e.label}</span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[10px] text-muted-foreground">o cuéntanos más</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Mic button */}
        <div className="flex justify-center">
          <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">
            <Mic size={14} />
            Grabar audio
          </button>
        </div>

        {/* Green confirmation card — right below mic */}
        <div className="rounded-3xl bg-calm/30 border border-calm/50 px-4 py-3">
          <div className="flex items-center justify-center gap-2 text-calm-foreground">
            <Heart size={14} className="fill-current shrink-0" />
            <span className="text-xs font-medium">Gracias por compartir que te sientes Feliz</span>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-1">Tu bienestar nos importa</p>
        </div>

        {/* Anonymous notice — pushed to bottom */}
        <div className="mt-auto rounded-2xl gradient-soft p-3">
          <p className="text-xs text-muted-foreground text-center">Tu check-in es anónimo y solo lo ves tú.</p>
        </div>
      </div>
    </PhoneFrame>
  );
};

// ── Understand mockup ─────────────────────────────────────────────────────────
export const MockupUnderstand = () => {
  const reasons = [
    { label: "Demasiada presión",             hint: "Sientes que no llegas a todo."          },
    { label: "Muchas tareas al mismo tiempo", hint: "Todo parece urgente al mismo tiempo."   },
    { label: "Sensación de no avanzar",       hint: "Haces mucho, pero sientes poco progreso." },
    { label: "Falta de pausas",               hint: "No has tenido tiempo real para desconectar." },
  ];

  return (
    <PhoneFrame>
      <div className="flex flex-col bg-background" style={{ height: 695, paddingTop: 56 }}>
        <div className="flex items-center gap-3 px-5 py-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-muted">
            <ArrowLeft size={18} className="text-foreground" />
          </div>
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-secondary/15">
            <Brain size={18} className="text-secondary" />
          </div>
          <span className="text-sm font-semibold text-foreground">Entender lo que pasa</span>
        </div>
        <div className="px-5 mt-3 space-y-3">
          {reasons.map((r, i) => (
            <div
              key={r.label}
              className={cn(
                "flex items-start gap-3 p-4 rounded-3xl border text-left",
                i === 1 ? "border-primary bg-primary/5" : "border-border bg-card"
              )}
            >
              <span className={cn(
                "mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center",
                i === 1 ? "border-primary" : "border-muted-foreground/40"
              )}>
                {i === 1 && <span className="w-2.5 h-2.5 rounded-full bg-primary" />}
              </span>
              <span className="flex-1">
                <span className="block text-sm font-medium text-foreground">{r.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{r.hint}</span>
              </span>
            </div>
          ))}
          <button className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-primary text-primary-foreground text-sm font-medium mt-2">
            <ChevronRight size={16} />
            Ver qué puedo hacer
          </button>
        </div>
      </div>
    </PhoneFrame>
  );
};

// ── Tools mockup ─────────────────────────────────────────────────────────────
export const MockupTools = () => {
  const tools = [
    { title: "Un paso pequeño",         hint: "¿Qué puedes hacer ahora?",   icon: Zap,       bg: "bg-calm"     },
    { title: "Ordena tu mente",         hint: "Vacía lo que ocupa espacio.", icon: BrainIcon, bg: "bg-muted"    },
    { title: "Respira antes de seguir", hint: "Reset de 60 segundos.",       icon: Wind,      bg: "bg-focus/40" },
    { title: "Cambia el foco",          hint: "Recupera perspectiva.",       icon: Compass,   bg: "bg-pause/40" },
  ];

  return (
    <PhoneFrame>
      <div className="flex flex-col bg-background" style={{ height: 695, paddingTop: 56 }}>
        <div className="flex items-center gap-3 px-5 py-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-muted">
            <ArrowLeft size={18} className="text-foreground" />
          </div>
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-calm">
            <CircleCheck size={18} className="text-calm-foreground" />
          </div>
          <span className="text-sm font-semibold text-foreground">¿Qué puedo hacer?</span>
        </div>
        <div className="px-5 mt-3 space-y-3">
          {tools.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="flex items-center gap-4 p-4 rounded-3xl border border-border bg-card">
                <div className={cn("w-11 h-11 rounded-full flex items-center justify-center shrink-0", t.bg)}>
                  <Icon size={20} className="text-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{t.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.hint}</p>
                </div>
                <ChevronRight size={16} className="text-muted-foreground shrink-0" />
              </div>
            );
          })}
        </div>
      </div>
    </PhoneFrame>
  );
};

export const MockupDashboard = () => {
  // Hand-crafted artistic bezier paths
  // SVG y=0 is top. Stress goes DOWN (top→bottom = high→low = improving).
  // Energy goes UP (bottom→top = low→high = improving). They cross in the middle.
  const stressLine = "M0,38 C45,52 75,18 125,42 C168,62 182,32 225,65 C260,80 300,58 340,78";
  const energyLine = "M0,86 C45,68 75,96 125,78 C168,58 182,82 225,62 C260,42 300,62 340,36";
  const fillPath   = `${stressLine} L340,92 L0,92 Z`;

  return (
    <BrowserFrame>
      <div className="p-4 space-y-3">
        <div className="rounded-2xl bg-card border border-border p-4 shadow-[var(--shadow-soft)]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-muted-foreground">Tendencia emocional · esta semana</p>
              <p className="text-sm font-semibold text-foreground">Estrés del equipo</p>
            </div>
            <span className="text-[10px] gradient-soft px-2 py-1 rounded-full text-muted-foreground font-medium">Anónimo</span>
          </div>
          <svg viewBox="0 0 340 100" className="w-full h-auto">
            <defs>
              <linearGradient id="mock-stress-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.2" />
                <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={fillPath} fill="url(#mock-stress-fill)" />
            <path d={stressLine} fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d={energyLine} fill="none" stroke="var(--color-secondary)" strokeWidth="2" strokeDasharray="5 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary" /><span className="text-xs text-muted-foreground">Estrés</span></div>
            <div className="flex items-center gap-1.5"><span className="w-3 h-px border-t-2 border-dashed border-secondary" /><span className="text-xs text-muted-foreground">Energía</span></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-card border border-border p-3 shadow-[var(--shadow-soft)]">
            <p className="text-xs text-muted-foreground">Engagement</p>
            <p className="text-xl font-semibold text-foreground mt-1">72<span className="text-sm text-muted-foreground">%</span></p>
            <p className="text-xs text-calm-foreground mt-1">↑ 8% vs. semana anterior</p>
          </div>
          <div className="rounded-2xl gradient-soft p-3 shadow-[var(--shadow-soft)]">
            <p className="text-xs text-foreground/70">Riesgo de burnout</p>
            <p className="text-xl font-semibold text-foreground mt-1">Bajo</p>
            <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
              <div className="h-full w-1/4 gradient-brand rounded-full" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {[
            { label: "Calma",     value: "38%", badge: "bg-emotion-calm     text-emotion-calm-fg"      },
            { label: "Energía",   value: "26%", badge: "bg-emotion-happy    text-emotion-happy-fg"     },
            { label: "Estrés",    value: "21%", badge: "bg-emotion-stressed text-emotion-stressed-fg"  },
            { label: "Cansancio", value: "15%", badge: "bg-emotion-tired    text-emotion-tired-fg"     },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-card border border-border p-3 shadow-[var(--shadow-soft)]">
              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${s.badge}`}>{s.label}</span>
              <p className="mt-2 text-base font-semibold text-foreground">{s.value}</p>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  );
};
